import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import "./actionConfirmation.css";

export type ActionOutcome = {
  variant: "success" | "attention" | "uncertain";
  title: string;
  description?: ReactNode;
  details?: Array<{ label?: string; value: ReactNode }>;
  action?: ReactNode;
};

/** Focus without jumping, scrolling only when the result is outside the viewport. */
export function revealActionConfirmation(element: HTMLElement, focus = true) {
  const bounds = element.getBoundingClientRect();
  const outside = bounds.top < 0 || bounds.bottom > window.innerHeight;
  if (focus) element.focus({ preventScroll: true });
  if (outside) element.scrollIntoView?.({
    block: "nearest",
    behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
  });
}

export const ActionConfirmation = forwardRef<HTMLDivElement, ActionOutcome & {
  /** Long forms opt in; ordinary inline results need not move focus. */
  reveal?: boolean;
  announce?: boolean;
  onPresented?: () => void;
}>(function ActionConfirmation({ variant, title, description, details, action, reveal = false, announce = true, onPresented }, forwardedRef) {
  const titleId = useId();
  const descriptionId = useId();
  const localRef = useRef<HTMLDivElement | null>(null);
  const copyRef = useRef<HTMLDivElement | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const signature = useRef<string | null>(null);
  const frames = useRef<number[]>([]);
  useEffect(() => {
    // Snapshot facts after the DOM update. Interactive recovery controls are
    // excluded, so their changing labels do not repeat the announcement.
    const copy = copyRef.current?.cloneNode(true) as HTMLElement | undefined;
    copy?.querySelectorAll('button, a[role="button"], a.button, a.button-link, input, select, textarea').forEach((node) => node.remove());
    copy?.querySelectorAll("div, p, li, br").forEach((node) => node.append(" "));
    const message = `${title}. ${(copy?.textContent ?? "").replace(/\s+/g, " ").trim()}`;
    const nextSignature = `${variant}:${message}`;
    if (signature.current === nextSignature) return;
    signature.current = nextSignature;
    frames.current.forEach(cancelAnimationFrame);
    frames.current = [];
    setAnnouncement("");
    // Focused receipts use their full accessible description. Page and inline
    // receipts populate an initially empty polite region after a rendered frame.
    // These are separate paths, so attention is not both alerted and focused.
    frames.current.push(requestAnimationFrame(() => {
      if (reveal && localRef.current) revealActionConfirmation(localRef.current);
      else if (announce) {
        frames.current.push(requestAnimationFrame(() => {
          setAnnouncement(message);
          onPresented?.();
        }));
        return;
      }
      onPresented?.();
    }));
  });
  useEffect(() => () => frames.current.forEach(cancelAnimationFrame), []);
  return <><div className="action-confirmation-announcement" role="status" aria-live="polite" aria-atomic="true">{announcement}</div><div
    ref={(node) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    }}
    className={`action-confirmation action-confirmation--${variant}`}
    role="region"
    aria-labelledby={titleId}
    aria-describedby={description != null || details ? descriptionId : undefined}
    tabIndex={-1}
  >
    <span className="action-confirmation-icon" aria-hidden="true">{variant === "success" ? "✓" : variant === "attention" ? "!" : "?"}</span>
    <div className="action-confirmation-content">
      <h3 id={titleId}>{title}</h3>
      <div id={descriptionId} ref={copyRef}>
      {description != null && <div className="action-confirmation-description">{description}</div>}
      {details && <div className="action-confirmation-details">{details.map((detail, index) => <div key={index}>
        {detail.label && <strong>{detail.label}: </strong>}{detail.value}
      </div>)}</div>}
      </div>
      {action != null && <div className="action-confirmation-actions">{action}</div>}
    </div>
  </div></>;
});

type PublishOutcome = (outcome: ActionOutcome, options?: { announce?: boolean }) => boolean;
const OutcomeContext = createContext<PublishOutcome | null>(null);
const noHost: PublishOutcome = () => false;

/** Returns false outside a host so standalone tools can keep their own result. */
export function usePublishActionOutcome() { return useContext(OutcomeContext) ?? noHost; }
export function useHasActionOutcomeHost() { return useContext(OutcomeContext) !== null; }

/** A single persistent result, owned above drawers and asynchronous read refreshes. */
export function ActionConfirmationHost({ children, forwardOnUnmount = false, reveal = false }: {
  children: ReactNode;
  /** Drawers own their receipts while open, then hand the last result to the page. */
  forwardOnUnmount?: boolean;
  reveal?: boolean;
}) {
  const parent = useContext(OutcomeContext);
  const [result, setResult] = useState<{ outcome: ActionOutcome; sequence: number; announce: boolean } | null>(null);
  const sequence = useRef(0);
  const pending = useRef<ActionOutcome | null>(null);
  const presented = useRef(false);
  const origin = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef(parent);
  parentRef.current = parent;
  useEffect(() => () => {
    if (forwardOnUnmount && pending.current) parentRef.current?.(pending.current, { announce: !presented.current });
  }, [forwardOnUnmount]);
  const publish = useCallback<PublishOutcome>((outcome, options) => {
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    pending.current = outcome;
    presented.current = false;
    setResult({ outcome, sequence: ++sequence.current, announce: options?.announce ?? true });
    return true;
  }, []);
  return <OutcomeContext.Provider value={publish}>
    {result && <ActionConfirmation key={result.sequence} {...result.outcome} reveal={reveal} announce={result.announce} onPresented={() => { presented.current = true; }} action={<>
      {result.outcome.action}
      <button type="button" className="quiet" onClick={() => {
        pending.current = null;
        setResult(null);
        const previous = origin.current;
        if (previous?.isConnected && previous !== document.body && !previous.closest("[inert]") && !previous.matches(":disabled")) previous.focus({ preventScroll: true });
        else (contentRef.current?.querySelector<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]') ?? contentRef.current)?.focus({ preventScroll: true });
      }}>Dismiss confirmation</button>
    </>} />}
    <div ref={contentRef} tabIndex={-1}>{children}</div>
  </OutcomeContext.Provider>;
}
